import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../state/AuthContext';
import { listedPositions } from '../content/positions';
import { GoogleIcon } from '../components/GoogleIcon';
import { api } from '../api/client';
import { routeForFurthestProgress } from '../lib/progressRoutes';
import { availabilityFor, positionLockMessage, useApplicationAvailability, useOpenPositions } from '../lib/applicationAvailability';
import { fillMarkets, useFieldMarkets } from '../lib/fieldMarkets';
import type { ProgressData } from '../state/ProgressContext';

const ERROR_MESSAGES: Record<string, string> = {
  invalid_state: 'Your sign-in attempt could not be verified. Please try again.',
  email_unverified: 'Your Google account email is not verified. Please verify it with Google and try again.',
  oauth_failed: 'Sign-in with Google failed. Please try again.',
};

export function JobsFeed() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const authError = params.get('authError');
  const [copiedPositionId, setCopiedPositionId] = useState<string | null>(null);
  const { availability, loading: availabilityLoading } = useApplicationAvailability(Boolean(user) && !user?.isAdmin);
  // Until both the session and its availability are known, no apply button can
  // be trusted either way, so none of them are clickable yet.
  const checkingAvailability = loading || availabilityLoading;
  // Only the positions the admin has open (Admin → Positions) are listed at all.
  const { openRoleIds, hiddenRoleIds, loading: positionsLoading, error: positionsError } = useOpenPositions();
  // A position hidden from the public is left off this page; its own page still works by link.
  const openPositions = openRoleIds ? listedPositions.filter((position) => openRoleIds.has(position.roleId) && !hiddenRoleIds.has(position.roleId)) : [];
  const openPositionCount = openPositions.length;
  // The field position's summary names the markets it is open in; they come from the one list
  // the application itself accepts, never from a copy kept here.
  const markets = useFieldMarkets();

  async function continueApplication() {
    if (!user) return;
    if (user.isAdmin) return navigate('/admin');
    const progress = await api.get<ProgressData>('/api/progress');
    navigate(routeForFurthestProgress(progress));
  }

  async function sharePosition(positionId: string) {
    const shareUrl = new URL(`/jobs/${positionId}`, window.location.origin);
    const link = shareUrl.toString();
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
    } else {
      const input = document.createElement('textarea');
      input.value = link; input.style.position = 'fixed'; input.style.opacity = '0';
      document.body.appendChild(input); input.select(); document.execCommand('copy'); input.remove();
    }
    setCopiedPositionId(positionId);
    window.setTimeout(() => setCopiedPositionId((current) => current === positionId ? null : current), 2000);
  }

  return (
    <div className="jobs-page">
      <header className="jobs-nav">
        <a className="jobs-brand" href="https://apply.krovance.com/" aria-label="Krovance careers home">Krovance<span>Careers</span></a>
        <div className="jobs-nav-actions">
          {loading ? <span className="jobs-auth-loading">Checking sign-in…</span> : user ? (
            <>
              {user.pictureUrl && <img className="jobs-avatar" src={user.pictureUrl} alt="" />}
              <span className="jobs-user-name">{user.name}</span>
              <button className="jobs-signin" type="button" onClick={() => void continueApplication()}>{user.isAdmin ? 'Open dashboard' : 'Continue application'}</button>
            </>
          ) : <a className="jobs-signin" href="/api/auth/google"><GoogleIcon /> Sign in with Google</a>}
        </div>
      </header>

      <main className="jobs-main">
        <section className="jobs-hero">
          <p className="jobs-eyebrow">WORK WITH KROVANCE</p>
          <h1>Find your next opportunity.</h1>
          <p>Commission-only sales positions. No experience required - we train you.</p>
        </section>
        {authError && <div className="alert error jobs-auth-error" role="alert">{ERROR_MESSAGES[authError] ?? 'Sign-in failed. Please try again.'}</div>}
        <section className="jobs-feed" aria-labelledby="open-positions-heading">
          <div className="jobs-feed-heading">
            <div><h2 id="open-positions-heading">Open positions</h2><p>{openPositionCount} {openPositionCount === 1 ? 'opportunity' : 'opportunities'}</p></div>
            <span>Newest first</span>
          </div>
          {positionsLoading && <p className="loading-state">Loading open positions…</p>}
          {positionsError && <div className="alert error" role="alert">Open positions could not be loaded. Please refresh the page.</div>}
          {!positionsLoading && !positionsError && openPositionCount === 0 && <p>There are no open positions right now. Please check back soon.</p>}
          {openPositions.map(({ id, roleId, listing: job }) => {
            const positionState = availabilityFor(availability, roleId);
            const lockedMessage = positionLockMessage(roleId, availability, openRoleIds);
            return (
            <article className={`job-card${lockedMessage ? ' locked' : ''}`} id={id} key={id}>
              <div className="job-card-copy">
                <p className="job-company">Krovance</p>
                <h3>{job.title}</h3>
                <div className="job-meta"><span>{job.location}</span><span>{job.type}</span></div>
                <p className="job-summary">{fillMarkets(job.summary, markets)}</p>
                <div className="job-tags">{job.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                {lockedMessage && <p className="job-card-locked-note">{lockedMessage}</p>}
              </div>
              <div className="job-card-actions">
                <button className="job-apply" type="button" disabled={checkingAvailability || Boolean(lockedMessage)} title={lockedMessage ?? undefined} onClick={() => navigate(`/jobs/${id}`)}>
                  {checkingAvailability ? 'Checking…' : lockedMessage ? 'Unavailable' : positionState?.started ? 'Continue application' : 'View position'}
                </button>
                <button className="job-share" type="button" onClick={() => void sharePosition(id)} aria-label={`Copy share link for ${job.title}`} title={copiedPositionId === id ? 'Link copied' : 'Copy share link'}>
                  {copiedPositionId === id ? <CheckIcon /> : <ShareIcon />}
                </button>
                <span className="sr-only" role="status" aria-live="polite">{copiedPositionId === id ? 'Application link copied' : ''}</span>
              </div>
            </article>
            );
          })}
        </section>
      </main>
    </div>
  );
}

function ShareIcon() {
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4" /></svg>;
}

function CheckIcon() {
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>;
}
